import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import * as taskService from '../services/taskService';
import { testBackendConnection, testMongoDBConnection } from '../services/testConnection';

const StatusBar = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  z-index: 1000;
  padding: 5px 10px;
  background-color: ${props => {
    if (props.mockData) return '#FFA000'; // Amber for mock data
    if (props.apiConnected && props.dbConnected) return '#34A853'; // Green for fully connected
    if (props.apiConnected && !props.dbConnected) return '#FBBC05'; // Yellow for API connected but DB offline
    return '#EA4335'; // Red for fully disconnected
  }};
  color: white;
  font-size: 12px;
  border-bottom-left-radius: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: white;
`;

const TestButton = styled.button`
  background: none;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.5);
  color: white;
  margin-left: 5px;
  padding-left: 5px;
  cursor: pointer;
  font-size: 11px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BackendStatus = () => {
  const [status, setStatus] = useState({
    apiConnected: false,
    dbConnected: false,
    mockData: false,
    lastCheck: null,
    message: 'Checking connection...'
  });
  
  // Get initial connection status
  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Test API connection
        const apiConnected = await testBackendConnection();
        
        // Test MongoDB connection if API is available
        let dbConnected = false;
        let mockData = false;
        
        if (apiConnected) {
          // Try to get boards to check if we're getting mock data
          try {
            const boardsResponse = await fetch(`${taskService.getApiUrl()}/boards`);
            const boardsData = await boardsResponse.json();
            
            // Check if any of the responses indicate mock data
            if (Array.isArray(boardsData) && boardsData.length > 0) {
              mockData = boardsData.some(board => board._isMock === true);
            }
            
            // Test DB connection more directly
            dbConnected = await testMongoDBConnection();
            
          } catch (dbError) {
            console.warn('Error checking MongoDB status:', dbError);
            dbConnected = false;
          }
        }
        
        // Set appropriate message based on status
        let statusMessage = 'Disconnected';
        
        if (apiConnected && dbConnected) {
          statusMessage = 'Fully Connected';
        } else if (apiConnected && !dbConnected) {
          statusMessage = 'API Connected, DB Offline';
        } else if (mockData) {
          statusMessage = 'Using Mock Data';
        }
        
        setStatus({
          apiConnected,
          dbConnected,
          mockData,
          lastCheck: new Date(),
          message: statusMessage
        });
        
        console.log('Connection status:', { apiConnected, dbConnected, mockData });
      } catch (error) {
        setStatus({
          apiConnected: false,
          dbConnected: false,
          mockData: false,
          lastCheck: new Date(),
          message: 'Connection failed'
        });
        
        console.error('Connection test failed:', error);
      }
    };
    
    checkStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Force a manual connection test
  const handleTestConnection = async () => {
    setStatus(prev => ({ ...prev, message: 'Testing connection...' }));
    
    try {
      // Test API connection
      const apiConnected = await testBackendConnection();
      
      // Test MongoDB connection if API is available
      let dbConnected = false;
      let mockData = false;
      
      if (apiConnected) {
        // Try to get boards to check if we're getting mock data
        try {
          const boardsResponse = await fetch(`${taskService.getApiUrl()}/boards`);
          const boardsData = await boardsResponse.json();
          
          // Check if any of the responses indicate mock data
          if (Array.isArray(boardsData) && boardsData.length > 0) {
            mockData = boardsData.some(board => board._isMock === true);
          }
          
          // Test DB connection more directly
          dbConnected = await testMongoDBConnection();
          
        } catch (dbError) {
          console.warn('Error checking MongoDB status:', dbError);
          dbConnected = false;
        }
      }
      
      // Set appropriate message based on status
      let statusMessage = 'Disconnected';
      
      if (apiConnected && dbConnected) {
        statusMessage = 'Fully Connected';
      } else if (apiConnected && !dbConnected) {
        statusMessage = 'API Connected, DB Offline';
      } else if (mockData) {
        statusMessage = 'Using Mock Data';
      }
      
      setStatus({
        apiConnected,
        dbConnected,
        mockData,
        lastCheck: new Date(),
        message: statusMessage
      });
      
      console.log('Connection status:', { apiConnected, dbConnected, mockData });
    } catch (error) {
      setStatus({
        apiConnected: false,
        dbConnected: false,
        mockData: false,
        lastCheck: new Date(),
        message: 'Connection failed'
      });
      
      console.error('Connection test failed:', error);
    }
  };
  
  return (
    <StatusBar 
      apiConnected={status.apiConnected} 
      dbConnected={status.dbConnected}
      mockData={status.mockData}
    >
      <StatusDot />
      {status.message}
      <TestButton onClick={handleTestConnection}>Test</TestButton>
    </StatusBar>
  );
};

export default BackendStatus; 